/**
 * Main chat component - adapted from prototype design
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { chatAPI, autoragAPI } from '../services/api';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showExamples, setShowExamples] = useState(true);
  const [searchMode, setSearchMode] = useState('ai'); // 'ai' or 'search'
  const [expandedSource, setExpandedSource] = useState(null);
  const [examplesCollapsed, setExamplesCollapsed] = useState(false);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const { user, logout } = useAuth();

  // Example questions for Budget 2025
  const exampleQuestions = [
    "What are the main health investments in Budget 2025?",
    "How much funding is allocated to education?",
    "What are the key infrastructure projects?",
    "What tax changes are proposed in Budget 2025?",
    "How does Budget 2025 address climate change?",
  ];

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus textarea when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Auto-collapse examples on mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768;
      setExamplesCollapsed(isMobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString(),
    };

    // Add user message and clear input
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setShowExamples(false);

    try {
      // Send to API based on search mode
      let response;
      if (searchMode === 'ai') {
        response = await chatAPI.sendMessage(
          messageText.trim(),
          messages,
          10
        );
      } else {
        // Use direct search for faster results
        const searchResponse = await autoragAPI.search(messageText.trim());
        response = {
          response: formatSearchResults(searchResponse),
          references: searchResponse.data || searchResponse.sources || [],
          total_results_found: searchResponse.data?.length || searchResponse.sources?.length || 0
        };
      }

      // Create assistant message
      const assistantMessage = {
        role: 'assistant',
        content: response.response,
        references: response.references || [],
        timestamp: new Date().toISOString(),
        totalResults: response.total_results_found || 0,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to send message. Please try again.');
      
      // Add error message
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again or contact support if the issue persists.',
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleExampleClick = (question) => {
    handleSendMessage(question);
  };

  const renderMarkdown = (content) => {
    const html = marked(content, {
      breaks: true,
      gfm: true,
    });
    return DOMPurify.sanitize(html);
  };

  const formatSearchResults = (searchResponse) => {
    // Check for data array (AutoRAG search format)
    const results = searchResponse.data || searchResponse.sources || [];
    
    if (results.length === 0) {
      return "No documents found matching your search.";
    }
    
    const formattedResults = results.map((source, idx) => {
      const filename = source.filename || extractFilename(source);
      const score = source.score ? `(${(source.score * 100).toFixed(1)}% match)` : '';
      const content = source.content?.[0]?.text || source.content?.substring(0, 150) || 'No preview available';
      return `${idx + 1}. **${filename}** ${score}\n   ${content.substring(0, 150)}...`;
    }).join('\n\n');
    
    return `Found ${results.length} relevant documents:\n\n${formattedResults}`;
  };

  const extractFilename = (source) => {
    // Try different possible field names for the filename
    return source.filename || 
           source.document_name || 
           source.metadata?.filename ||
           source.metadata?.document_name ||
           source.metadata?.source ||
           source.url ||
           'Document';
  };

  const renderReferences = (references) => {
    if (!references || references.length === 0) return null;

    return (
      <div className="sources">
        <div className="mb-2">
          <strong>Referenced Sources:</strong>
        </div>
        {references.map((ref, index) => {
          const filename = extractFilename(ref);
          const isExpanded = expandedSource === index;
          
          return (
            <div key={index} className="source-item">
              <div 
                className="d-flex justify-content-between align-items-start"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandedSource(isExpanded ? null : index)}
              >
                <div className="flex-grow-1">
                  <strong>
                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-2`} 
                       style={{ fontSize: '0.8em' }}></i>
                    {filename}
                  </strong>
                  {ref.chunk_id && (
                    <span className="text-muted ms-2">Chunk: {ref.chunk_id}</span>
                  )}
                </div>
                <div className="text-muted small">
                  {ref.score ? (ref.score * 100).toFixed(1) + '% match' : 'Relevant'}
                </div>
              </div>
              {ref.content && (
                <div className={`mt-2 small ${isExpanded ? '' : 'text-muted'}`}>
                  {isExpanded ? (
                    <div className="p-2 bg-light rounded">
                      <div className="mb-1">
                        <strong>Content:</strong>
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {ref.content}
                      </div>
                      {ref.metadata && Object.keys(ref.metadata).length > 0 && (
                        <div className="mt-2 pt-2 border-top">
                          <strong>Metadata:</strong>
                          <div className="mt-1">
                            {Object.entries(ref.metadata).map(([key, value]) => (
                              <div key={key}>
                                <span className="text-muted">{key}:</span> {value}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>{ref.content.substring(0, 200)}... <em>(click to expand)</em></span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="chat-container">
      {/* Header */}
      <div className="chat-header">
        <div className="header-title">
          <h1 className="page-title">Budget 2025 Assistant</h1>
          <small className="text-light opacity-75 d-none d-sm-block">
            New Zealand Budget 2025 Information
          </small>
        </div>
        <div className="header-controls d-flex align-items-center gap-2 gap-md-3">
          {/* Search Mode Toggle */}
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${searchMode === 'ai' ? 'btn-light' : 'btn-outline-light'}`}
              onClick={() => setSearchMode('ai')}
              title="AI Chat Mode - Get conversational answers"
            >
              <i className="fas fa-robot d-none d-sm-inline me-1"></i>
              <span className="d-inline d-sm-none">AI</span>
              <span className="d-none d-sm-inline">AI Chat</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm ${searchMode === 'search' ? 'btn-light' : 'btn-outline-light'}`}
              onClick={() => setSearchMode('search')}
              title="Document Search - Find specific documents"
            >
              <i className="fas fa-search d-none d-sm-inline me-1"></i>
              Search
            </button>
          </div>
          <span className="text-light small d-none d-md-inline">
            <i className="fas fa-user me-1"></i>
            {user?.email}
            {user?.is_admin && (
              <span className="badge bg-warning text-dark ms-1">Admin</span>
            )}
          </span>
          <button
            className="btn btn-outline-light btn-sm"
            onClick={logout}
            title="Sign Out"
          >
            <i className="fas fa-sign-out-alt"></i>
            <span className="d-none d-sm-inline ms-1">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main chat area */}
      <div className="chat-main">
        {/* Messages */}
        <div className="messages">
          {messages.length === 0 && showExamples && (
            <div className="text-center mb-4">
              <h3 className="text-muted mb-3">
                <i className={`fas fa-${searchMode === 'ai' ? 'comments' : 'search'} me-2`}></i>
                {searchMode === 'ai' ? 'Ask me about Budget 2025' : 'Search Budget 2025 Documents'}
              </h3>
              <p className="text-muted">
                {searchMode === 'ai' 
                  ? 'I can help you find information about health investments, education funding, infrastructure projects, and other Budget 2025 initiatives.'
                  : 'Search through all Budget 2025 documents to find specific information quickly.'}
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <div className="message-avatar">
                {message.role === 'user' ? (
                  <i className="fas fa-user"></i>
                ) : (
                  <i className="fas fa-robot"></i>
                )}
              </div>
              <div className="message-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(message.content)
                  }}
                />
                {message.references && renderReferences(message.references)}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="message assistant">
              <div className="message-avatar">
                <i className="fas fa-robot"></i>
              </div>
              <div className="message-content">
                <div className="loading">
                  <span>{searchMode === 'ai' ? 'Thinking' : 'Searching'}</span>
                  <div className="loading-dots">
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="alert alert-danger mx-3" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <button
                type="button"
                className="btn-close float-end"
                onClick={() => setError(null)}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Example questions */}
        {showExamples && messages.length === 0 && (
          <div className={`examples-section ${examplesCollapsed ? 'collapsed' : ''}`}>
            <div 
              className="examples-header"
              onClick={() => setExamplesCollapsed(!examplesCollapsed)}
              style={{ cursor: 'pointer' }}
            >
              <span>
                <i className="fas fa-lightbulb me-2"></i>
                Try these example questions:
              </span>
            </div>
            {exampleQuestions.map((question, index) => (
              <div
                key={index}
                className="example-item"
                onClick={() => handleExampleClick(question)}
              >
                <i className="fas fa-comment-dots me-2 text-muted"></i>
                {question}
              </div>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="input-area">
          <div className="input-group">
            <textarea
              ref={textareaRef}
              className="form-control"
              placeholder={searchMode === 'ai' ? "Ask me about Budget 2025..." : "Search for documents..."}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              rows="1"
              style={{ 
                resize: 'none',
                minHeight: '50px',
                maxHeight: '120px'
              }}
            />
            <button
              className="btn btn-primary"
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading}
              type="button"
            >
              {isLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </button>
          </div>
          <div className="mt-2 text-center">
            <small className="text-muted">
              <i className="fas fa-shield-alt me-1"></i>
              Powered by Cloudflare AutoRAG
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;